import {
  oneDayInMilliseconds,
  oneHourInMilliseconds,
  oneMinuteInMilliseconds
} from "../shared/constants";
import {intervalToDuration, isAfter, isBefore, isEqual} from "date-fns";
import {ObservableList} from "../stores/observables/observableList";
import {KeyValue} from "../stores/observables/keyValue";

export class dateUtils {
  static timeDifference = (startDate?: Date | null, endDate?: Date | null) => {
    if (!startDate || !endDate) {
      return {days: 0, hours: 0, minutes: 0};
    }

    // Calculate time difference in milliseconds
    const timeDifference = endDate.getTime() - startDate.getTime();

    // Calculate days and hours and minutes
    const days = Math.floor(timeDifference / oneDayInMilliseconds);
    const hours = Math.floor(
      (timeDifference % oneDayInMilliseconds) / oneHourInMilliseconds
    );
    const minutes = Math.floor(
      (timeDifference % oneHourInMilliseconds) / oneMinuteInMilliseconds
    );
    return {days, hours, minutes};
  };
}

export const isToday = (someDate: Date): boolean => {
  const today = new Date();
  return (
    someDate.getDate() === today.getDate() &&
    someDate.getMonth() === today.getMonth() &&
    someDate.getFullYear() === today.getFullYear()
  );
};

export const trimSeconds = (value: Date): Date | null => {
  if (value == null) return null;
  let newDate = new Date(value);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);
  return newDate;
};

export const getDateOnly = (date?: Date | null): Date | null => {
  if (!date) {
    return null;
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const checkDatesEqual = (
  dateOne?: Date | null,
  dateTwo?: Date | null
) => {
  const dateOnlyDateOne = getDateOnly(dateOne);
  const dateOnlyDateTwo = getDateOnly(dateTwo);
  if (dateOnlyDateOne && dateOnlyDateTwo) {
    return isEqual(dateOnlyDateOne, dateOnlyDateTwo);
  }
  return false;
};

export const isDateOneSameOrAfterDateTwo = (
  dateOne?: Date,
  dateTwo?: Date
): boolean => {
  return (
    checkDatesEqual(dateOne, dateTwo) || isDateOneAfterDateTwo(dateOne, dateTwo)
  );
};

export const isDateOneAfterDateTwo = (
  dateOne?: Date,
  dateTwo?: Date
): boolean => {
  const dateOnlyDateOne = getDateOnly(dateOne);
  const dateOnlyDateTwo = getDateOnly(dateTwo);
  if (dateOnlyDateOne && dateOnlyDateTwo) {
    return isAfter(dateOnlyDateOne, dateOnlyDateTwo);
  }

  return false;
};

export const isDateOneSameOrBeforeDateTwo = (
  dateOne?: Date,
  dateTwo?: Date
): boolean => {
  return (
    checkDatesEqual(dateOne, dateTwo) ||
    isDateOneBeforeDateTwo(dateOne, dateTwo)
  );
};

export const isDateOneBeforeDateTwo = (
  dateOne?: Date,
  dateTwo?: Date
): boolean => {
  const dateOnlyDateOne = getDateOnly(dateOne);
  const dateOnlyDateTwo = getDateOnly(dateTwo);
  if (dateOnlyDateOne && dateOnlyDateTwo) {
    return isBefore(dateOnlyDateOne, dateOnlyDateTwo);
  }

  return false;
};

export const isDateOneBetweenTwoDates = (
  dateOne: Date,
  startDate: Date,
  endDate: Date
): boolean => {
  return (
    isDateOneSameOrAfterDateTwo(dateOne, startDate) &&
    isDateOneSameOrBeforeDateTwo(dateOne, endDate)
  );
};

export function parseDuration(
  duration?: string,
  isIndependentEventRelease?: boolean
) {
  let weeks = 0,
    days = 0,
    hours = 0,
    minutes = 0;
  if (duration) {
    // Ensure this is a valid 8601 period
    if (!duration.startsWith("P")) {
      throw Error("Could not parse unrecognised duration: " + duration);
    }

    let previousCharIsDigit = false;
    let currentValue = "";
    let isParsingTime = false;

    for (let i = 0; i < duration.length; i++) {
      const currentChar = duration.charAt(i).toUpperCase();

      const isAlpha = Boolean(currentChar.match(/[A-Z]/));
      const isDigit = Boolean(currentChar.match(/[0-9]/));

      // 'T" is the divider between years/months/days/weeks and time (hours, minutes, seconds)
      if (currentChar === "T") {
        isParsingTime = true;
        continue;
      }

      if (isAlpha && previousCharIsDigit) {
        // when we switch from digits to letters, we know we have a "segment"
        const time = parseInt(currentValue);

        // set the correct value based on the current identifier
        switch (currentChar) {
          case "M":
            if (isParsingTime) {
              minutes = time;
            }
            break;
          case "D":
            days = time;
            break;
          case "H":
            hours = time;
            break;
          default:
        }

        // Reset the values
        currentValue = "";
      } else if (isDigit) {
        // Gather digits for this section
        currentValue += currentChar;
      }
      previousCharIsDigit = isDigit;
    }
  }

  // converts to weeks and days
  if (isIndependentEventRelease && days > 6) {
    weeks = Math.floor(days / 7);
    days = (days - weeks * 7) % 7;
  }

  return {weeks, days, hours, minutes};
}

/**
 * Get month key value pairs based on the provided month diff.
 * @param monthDiffs difference from the current month. i.e. [-1, 0, 1] will return previous, current and next month
 */
export const getMonthKeyValuePairs = (monthDiffs = [-1, 0, 1]) => {
  return new ObservableList<KeyValue>(
    monthDiffs.map((increment) => {
      const now = new Date();
      const exportCalendarDate = new Date(
        //Set date to 15 to avoid running into the following month unexpectedly.
        //i.e. on 31st Jan, new Date().setMonth(1) will become 3rd March
        now.setMonth(now.getMonth() + increment, 15)
      );
      const formattedExportCalendarDate = exportCalendarDate.toLocaleString(
        "default",
        {
          month: "long",
          year: "numeric"
        }
      ); // e.g. January 2022

      return new KeyValue(
        exportCalendarDate.toISOString(),
        formattedExportCalendarDate
      );
    }),
    null,
    (value) => {
      return value !== null && value !== undefined;
    }
  );
};

/*
 * Get the duration between two dates
 * @param startDate The start date
 * @param endDate The end date
 * @returns The duration between the two dates in string format
 * D, H, M are used to represent days, hours and minutes respectively
 */
export const getDuration = (startDate: Date, endDate: Date) => {
  const duration = intervalToDuration({start: startDate, end: endDate});
  let durationInString = "";

  if (duration.days) {
    durationInString += `${duration.days}D`;
  }
  if (duration.hours) {
    durationInString += `${duration.hours}H`;
  }
  if (duration.minutes) {
    durationInString += `${duration.minutes}M`;
  }

  return durationInString;
};
